import {
  Box,
  Button,
  Dialog,
  IconButton,
  Icons,
  iconSize,
  Typography,
  useFieldValidation,
  useSnackbar,
  webFonts,
  webPalette,
} from '@munifocus/ui';
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Kicker } from './Kicker';
import { emailError, phoneError, validationMessageKey } from '@munifocus/utils';
import { FieldInput, FieldLabel, formatPhoneInput } from './FormFields';
import { apiLang, isVerificationError, submitContact } from '../content/lead-capture';

interface ContactDialogContextValue {
  open: () => void;
  close: () => void;
  isOpen: boolean;
}

const Ctx = createContext<ContactDialogContextValue | null>(null);

/** Ids the dialog uses to name and describe itself, and to name its radio group. */
const DIALOG_TITLE_ID = 'contact-dialog-title';
const DIALOG_BODY_ID = 'contact-dialog-body';
const PREF_LABEL_ID = 'contact-dialog-pref-label';

export function useContactDialog() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useContactDialog must be used within ContactDialogProvider');
  return ctx;
}

export function ContactDialogProvider({ children }: { children: ReactNode }) {
  const [isOpen, setOpen] = useState(false);
  const open = useCallback(() => setOpen(true), []);
  const close = useCallback(() => setOpen(false), []);
  const value = useMemo(() => ({ open, close, isOpen }), [open, close, isOpen]);
  return (
    <Ctx.Provider value={value}>
      {children}
      <ContactDialog open={isOpen} onClose={close} />
    </Ctx.Provider>
  );
}

interface FormState {
  name: string;
  organization: string;
  email: string;
  phone: string;
  situation: string;
  pref: 'email' | 'phone' | 'either';
}

const empty: FormState = {
  name: '',
  organization: '',
  email: '',
  phone: '',
  situation: '',
  pref: 'email',
};

function ContactDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t, i18n } = useTranslation();
  const snackbar = useSnackbar();
  const [form, setForm] = useState<FormState>(empty);
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(false);

  // Listed in screen order: a failed submit focuses the first one.
  const emailCode = emailError(form.email);
  const phoneCode = phoneError(form.phone, { allowExtension: true });
  const v = useFieldValidation({
    name: form.name.trim() ? null : t('common.validation.required'),
    email: emailCode && t(validationMessageKey('email', emailCode)),
    phone: phoneCode && t(validationMessageKey('phoneExt', phoneCode)),
    situation: form.situation.trim() ? null : t('common.validation.required'),
  });

  const close = () => {
    onClose();
    setTimeout(() => {
      setSent(false);
      setError(false);
      v.reset();
      setForm(empty);
    }, 300);
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!v.validate()) return;
    setSubmitting(true);
    setError(false);
    try {
      const prefLabel = t(`web.modal.fields.pref_${form.pref}`);
      // In the submitter's language, which is also the `language` sent with the
      // message — a French message should not carry an English label.
      const message = `${form.situation.trim()}\n\n${t('web.modal.preferredContactLine', {
        pref: prefLabel,
      })}`;
      await submitContact({
        name: form.name.trim(),
        email: form.email.trim(),
        message,
        language: apiLang(i18n.language),
        topic: 'general',
        organization: form.organization.trim() || undefined,
        phone: form.phone.trim() || undefined,
      });
      setSent(true);
      // Surface the confirmation on the page too, so it persists after the
      // modal auto-closes (top-right snackbar via the shared SnackbarProvider).
      snackbar.success(t('web.modal.thanksTitle'));
      setTimeout(close, 1800);
    } catch (err) {
      setError(true);
      snackbar.error(t(isVerificationError(err) ? 'web.modal.verifyError' : 'web.modal.errorBody'));
    } finally {
      setSubmitting(false);
    }
  };

  const set =
    <K extends keyof FormState>(k: K) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const raw = e.target.value;
      const value = (
        k === 'phone' ? formatPhoneInput(raw, { allowExtension: true }) : raw
      ) as FormState[K];
      setForm((f) => ({ ...f, [k]: value }));
    };

  return (
    <Dialog
      open={open}
      onClose={close}
      maxWidth="sm"
      fullWidth
      // Without these the dialog had no accessible name at all — a screen
      // reader entering it announced only "dialog" (WCAG 4.1.2).
      aria-labelledby={DIALOG_TITLE_ID}
      aria-describedby={DIALOG_BODY_ID}
      slotProps={{
        paper: {
          sx: {
            backgroundColor: webPalette.cream,
            borderRadius: '14px',
            p: { xs: 3, md: '44px 48px' },
            maxWidth: 720,
            position: 'relative',
          },
        },
        backdrop: {
          sx: { backgroundColor: webPalette.overlayBackdrop, backdropFilter: 'blur(4px)' },
        },
      }}
    >
      <IconButton
        aria-label={t('common.a11y.close')}
        onClick={close}
        sx={{
          position: 'absolute',
          top: 22,
          right: 22,
          width: 38,
          height: 38,
          border: `1px solid ${webPalette.line}`,
          backgroundColor: webPalette.white,
          color: webPalette.muted,
          '&:hover': { backgroundColor: webPalette.parch, color: webPalette.ink },
        }}
      >
        <Icons.X size={iconSize.lg} />
      </IconButton>

      {sent ? (
        // The form is replaced by this panel and the dialog then auto-closes,
        // so the confirmation has to be announced rather than merely shown.
        <Box role="status" aria-live="polite" sx={{ textAlign: 'center', py: 4 }}>
          <Box
            sx={{
              width: 58,
              height: 58,
              borderRadius: '50%',
              backgroundColor: webPalette.teal,
              color: webPalette.greenDeep,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 2.25,
            }}
          >
            <Icons.Check size={iconSize.xl} />
          </Box>
          <Typography sx={{ fontFamily: webFonts.bulletin, fontWeight: 500, fontSize: 26 }}>
            {t('web.modal.thanksTitle')}
          </Typography>
          <Typography sx={{ color: webPalette.muted, mt: 1 }}>
            {t('web.modal.thanksBody')}
          </Typography>
        </Box>
      ) : (
        <Box component="form" onSubmit={onSubmit} noValidate>
          <Kicker sx={{ mb: 1.75 }}>{t('web.modal.kicker')}</Kicker>
          <Typography
            id={DIALOG_TITLE_ID}
            component="h2"
            sx={{
              fontFamily: webFonts.bulletin,
              fontWeight: 500,
              fontSize: 34,
              lineHeight: 1.1,
              letterSpacing: '-0.01em',
            }}
          >
            {t('web.modal.title')}
          </Typography>
          <Typography
            id={DIALOG_BODY_ID}
            sx={{
              color: webPalette.muted,
              fontSize: 15.5,
              mt: 1.5,
              lineHeight: 1.55,
              maxWidth: '48ch',
            }}
          >
            {t('web.modal.body')}
          </Typography>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              gap: '18px 22px',
              mt: 3.5,
            }}
          >
            <FieldInput
              label={t('web.modal.fields.name')}
              required
              value={form.name}
              onChange={set('name')}
              placeholder={t('web.modal.fields.namePh')}
              {...v.field('name')}
            />
            <FieldInput
              label={t('web.modal.fields.org')}
              value={form.organization}
              onChange={set('organization')}
              placeholder={t('web.modal.fields.orgPh')}
            />
            <FieldInput
              label={t('web.modal.fields.email')}
              type="email"
              inputMode="email"
              autoComplete="email"
              required
              value={form.email}
              onChange={set('email')}
              {...v.field('email')}
              placeholder={t('web.modal.emailPlaceholder')}
            />
            <FieldInput
              label={t('web.modal.fields.phone')}
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              value={form.phone}
              onChange={set('phone')}
              {...v.field('phone')}
              placeholder="506-269-0706"
            />
            <Box sx={{ gridColumn: { xs: 'auto', md: '1 / 3' } }}>
              <FieldInput
                label={t('web.modal.fields.situation')}
                required
                multiline
                minRows={4}
                value={form.situation}
                onChange={set('situation')}
                placeholder={t('web.modal.fields.situationPh')}
                {...v.field('situation')}
              />
            </Box>
            <Box sx={{ gridColumn: { xs: 'auto', md: '1 / 3' } }}>
              <FieldLabel id={PREF_LABEL_ID}>{t('web.modal.fields.pref')}</FieldLabel>
              {/*
                A set of radios needs a group with a name, otherwise the caption
                above it is just nearby text (WCAG 1.3.1 / 3.3.2).
              */}
              <Box
                role="radiogroup"
                aria-labelledby={PREF_LABEL_ID}
                sx={{ display: 'flex', gap: 3.25, flexWrap: 'wrap', mt: 0.5 }}
              >
                {(['email', 'phone', 'either'] as const).map((p) => (
                  <Box
                    key={p}
                    component="label"
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.125,
                      fontSize: 15,
                      fontWeight: 500,
                      cursor: 'pointer',
                    }}
                  >
                    <Box
                      component="input"
                      type="radio"
                      name="pref"
                      checked={form.pref === p}
                      onChange={() => setForm((f) => ({ ...f, pref: p }))}
                      sx={{ accentColor: webPalette.tealInk, width: 17, height: 17 }}
                    />
                    {t(`web.modal.fields.pref_${p}`)}
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>

          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={submitting}
            sx={{
              mt: 3.25,
              fontSize: 16,
              p: 2,
              backgroundColor: webPalette.green,
              '&:hover': { backgroundColor: webPalette.greenDeep },
            }}
          >
            {t('web.modal.submit')}
          </Button>
          {error && (
            <Typography
              role="alert"
              sx={{ color: webPalette.danger, fontSize: 14, mt: 1.5, textAlign: 'center' }}
            >
              {t('web.modal.errorBody')}
            </Typography>
          )}
        </Box>
      )}
    </Dialog>
  );
}

interface ContactTriggerProps {
  children: ReactNode;
  variant?: 'contained' | 'outlined' | 'text';
  color?: 'primary' | 'secondary';
  startIcon?: ReactNode;
  sx?: object;
}

export function ContactTrigger({
  children,
  variant = 'contained',
  color = 'primary',
  startIcon,
  sx,
}: ContactTriggerProps) {
  const { open } = useContactDialog();
  return (
    <Button variant={variant} color={color} startIcon={startIcon} onClick={open} sx={sx}>
      {children}
    </Button>
  );
}
